import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientSaveEstateStatusHouseholdSettings, useGraphqlClient } from '../../graphql';
import type { ScoreAssessmentList } from '../../generated/graphql';
import { saodKeys } from '../../ScoreAssessmentOnDemand';
import { scorecardKeys } from '../../Scorecard/queryKeys';
import { ESTATE_STATUS_PERCENT_SCALE } from '../constants';
import { estateStatusKeys } from '../queryKeys';
import { calculateEstateStatusSummary, cloneEstateStatusChecklist } from '../util';
import type { EstateStatus, SaveEstateStatusChecklistInput } from '../types';

function setEstateStatusScoreCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  householdID: string | undefined | null,
  firmID: string | undefined | null,
  score: number | null | undefined,
  updateScoreAssessments = true
) {
  const normalizedScore = score == null ? null : score / ESTATE_STATUS_PERCENT_SCALE;

  queryClient.setQueriesData({ queryKey: scorecardKeys.scorecard(householdID) }, (scorecard: unknown) => {
    if (!scorecard || typeof scorecard !== 'object') {
      return scorecard;
    }

    return {
      ...scorecard,
      es: normalizedScore,
    };
  });

  if (!updateScoreAssessments || !firmID) {
    return;
  }

  [saodKeys.forFirm(firmID, false), saodKeys.forFirm(firmID, true)].forEach((queryKey) => {
    queryClient.setQueryData<ScoreAssessmentList>(queryKey, (scoreAssessments) => {
      if (!scoreAssessments) {
        return scoreAssessments;
      }

      let didUpdate = false;
      const items = scoreAssessments.items.map((scoreAssessment) => {
        if (scoreAssessment.firmClient.household?.id !== householdID) {
          return scoreAssessment;
        }

        didUpdate = true;
        return {
          ...scoreAssessment,
          scorecard: {
            ...scoreAssessment.scorecard,
            es: normalizedScore,
          },
        };
      });

      return didUpdate ? { ...scoreAssessments, items } : scoreAssessments;
    });
  });
}

export function useSaveEstateStatusHouseholdSettings(
  householdID: string | undefined | null,
  firmID: string | undefined | null
) {
  const gqlClient = useGraphqlClient();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (input: SaveEstateStatusChecklistInput) => {
      if (!householdID) {
        return Promise.reject(new Error('Household ID is required to save Estate Status.'));
      }

      return clientSaveEstateStatusHouseholdSettings(gqlClient, { householdID, ...input });
    },
    {
      onMutate: async (input) => {
        if (!householdID) {
          return { previousEstateStatus: undefined, previousScoreAssessmentLists: undefined };
        }

        const scoreAssessmentQueryKeys = firmID
          ? [saodKeys.forFirm(firmID, false), saodKeys.forFirm(firmID, true)]
          : [];
        await Promise.all(scoreAssessmentQueryKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })));
        const previousScoreAssessmentLists = scoreAssessmentQueryKeys.flatMap((queryKey) =>
          queryClient.getQueriesData<ScoreAssessmentList>({ queryKey })
        );
        const queryKey = estateStatusKeys.householdSettings(householdID);
        const previousEstateStatus = queryClient.getQueryData<EstateStatus>(queryKey);

        if (!previousEstateStatus) {
          return { previousEstateStatus, previousScoreAssessmentLists };
        }

        const optimisticChecklist = cloneEstateStatusChecklist(previousEstateStatus.checklist).map((item) => {
          const savedItem = input.checklist.find((checklistItem) => checklistItem.id === item.id);

          if (!savedItem) {
            return item;
          }

          const optimisticAttachmentLinks = (savedItem.attachmentLinks ?? []).map((attachmentLink) => {
            const existingAttachmentLink = item.attachmentLinks.find(
              (currentAttachmentLink) => currentAttachmentLink.id === attachmentLink.id
            );

            return {
              ...existingAttachmentLink,
              id: attachmentLink.id,
              fileName: attachmentLink.fileName,
              url: attachmentLink.url,
              uploadedAt: existingAttachmentLink?.uploadedAt ?? null,
              uploadedByUserID: existingAttachmentLink?.uploadedByUserID ?? '',
            };
          });

          return {
            ...item,
            state: savedItem.state,
            lastReviewedAt: savedItem.lastReviewedAt ?? null,
            note: savedItem.note ?? '',
            attachmentLinks: optimisticAttachmentLinks,
          };
        });
        const optimisticSummary = calculateEstateStatusSummary(optimisticChecklist);
        const optimisticEstateStatus = {
          ...previousEstateStatus,
          checklist: optimisticChecklist,
          score: optimisticSummary.score,
          summary: optimisticSummary,
        };

        queryClient.setQueryData(queryKey, optimisticEstateStatus);
        setEstateStatusScoreCaches(queryClient, householdID, firmID, optimisticSummary.score);

        return { previousEstateStatus, previousScoreAssessmentLists };
      },
      onError: (_error, _input, context) => {
        if (!householdID) {
          return;
        }

        if (context?.previousEstateStatus) {
          queryClient.setQueryData(estateStatusKeys.householdSettings(householdID), context.previousEstateStatus);
          setEstateStatusScoreCaches(queryClient, householdID, firmID, context.previousEstateStatus.score, false);
        }

        context?.previousScoreAssessmentLists?.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      },
      onSuccess: async (savedEstateStatus) => {
        if (!householdID) {
          return;
        }

        if (savedEstateStatus) {
          queryClient.setQueryData(estateStatusKeys.householdSettings(householdID), savedEstateStatus);
        }

        if (typeof savedEstateStatus?.score === 'number' || savedEstateStatus?.score === null) {
          // Estate Status is not date-versioned, so update every cached scorecard variant for the household.
          setEstateStatusScoreCaches(queryClient, householdID, firmID, savedEstateStatus.score);
        }

        await queryClient.invalidateQueries({ queryKey: estateStatusKeys.householdSettings(householdID) });
        await queryClient.invalidateQueries({ queryKey: scorecardKeys.scorecard(householdID) });
      },
    }
  );

  return { saveEstateStatusHouseholdSettingsMutation: mutation };
}
